// src/pages/PurchaseReturnRecord/components/exportPurchaseReturnRecordCsv.js
import { formatToCompanyTimezone, numberFormatTax } from "../../../../../lib/functions";

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const money = (v) => Number(v || 0).toFixed(2);
const fmtDate = (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-");
const itemFamily = ["By Item", "By Group", "By Category", "By Tag"];

/**
 * @param {object} params
 * @param {Array}   params.list      - rows from Redux state.purchaseReturnRecord.list
 * @param {object}  params.filters   - filterType + recordType
 * @param {object}  params.authInfo  - for taxLabel + GST columns
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportPurchaseReturnRecordCsv = ({
  list,
  filters,
  authInfo,
  title    = "Purchase Return Record",
  filename = "purchase_return_record",
}) => {
  const isIndia      = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGst      = isIndia && isGstEnabled;
  const taxLabel     = isIndia ? "GST" : "VAT";
  const { filterType, recordType } = filters;
  const isItemBased  = itemFamily.includes(filterType);

  let headers = [];
  let buildRow;
  let buildTotalRow;

  // ── By Tag ────────────────────────────────────────────────────
  if (filterType === "By Tag") {
    headers = [
      "No.", "Voucher No", "Date", "Supplier", "Item Name", "Item Code",
      "Qty", "Rate", "Discount%", "VAT%", "Group", "Category", "Total",
      "Tag Group", "Tag Field",
    ];
    buildRow = (row, i) => [
      i + 1, row.vch_no || "", fmtDate(row.date),
      row.supplier || "", row.item_name || "", row.display_text || "",
      row.item_qty, money(row.item_rate),
      money(row.item_discount_per), money(row.item_tax_per),
      row.grpname || "", row.categoryName || "",
      money(row.item_total),
      row.group_name || "", row.field_name || "",
    ];

  // ── By Item / By Group / By Category ─────────────────────────
  } else if (isItemBased) {
    headers = [
      "No.", "Voucher No", "Date", "Supplier", "Item Name", "Item Code",
      "Qty", "Rate", "Discount%", "VAT%", "Group", "Category", "Total",
    ];
    buildRow = (row, i) => [
      i + 1, row.vch_no || "", fmtDate(row.date),
      row.supplier || "", row.item_name || "", row.display_text || "",
      row.item_qty, money(row.item_rate),
      money(row.item_discount_per), money(row.item_tax_per),
      row.grpname || "", row.categoryName || "",
      money(row.item_total),
    ];

  // ── With Item ─────────────────────────────────────────────────
  } else if (recordType === "with item") {
    headers = [
      "No.", "Voucher No", "Date", "Supplier", "Created By",
      "Item", "Rate", "Discount%", "VAT%", "Qty", "Total",
    ];
    buildRow = (row, i) => [
      i + 1, row.vch_no || "", fmtDate(row.date),
      row.supplier || "", row.createdBy || "",
      row.item || "", row.rate,
      row.discount || "", row.vat || "",
      row.qty, row.total,
    ];

  // ── Without Item (summary) ────────────────────────────────────
  } else {
    headers = [
      "No.", "Voucher No", "Date", "Supplier / Creditor", "Created By",
      "Sub Total", "Discount", taxLabel,
      ...(showGst ? ["CGST", "SGST", "IGST"] : []),
      "Other Service", "Total", "Paid", "Due",
    ];
    buildRow = (row, i) => [
      i + 1,
      row.vch_no    || "",
      fmtDate(row.date),
      row.supplier  || "",
      row.createdBy || "",
      money(row.subtotal),
      money(row.discount),
      money(row.vat),
      ...(showGst ? [
        money(row.cgst_amount),
        money(row.sgst_amount),
        money(row.igst_amount),
      ] : []),
      money(row.service),
      money(row.total),
      money(row.paid),
      money(row.due),
    ];
  }

  // ── Totals row ────────────────────────────────────────────────
  if (recordType === "with item") {
    const masterQty = list.reduce((s, r) => s + Number(r.pur_r_qty || 0), 0);
    const baseQty   = list.reduce((s, r) => s + Number(r.pur_r_qty || 0), 0);
    const total     = list.reduce((s, r) => s + Number(r.total     || 0), 0);
    buildTotalRow = () => {
      const row = Array(headers.length).fill("");
      row[0]  = "Grand Total:";
      row[headers.length - 3] = `Master Qty: ${money(masterQty)}`;
      row[headers.length - 2] = `Base Qty: ${money(baseQty)}`;
      row[headers.length - 1] = money(total);
      return row;
    };

  } else if (isItemBased) {
    const total = list.reduce((s, r) => s + Number(r.item_total || r.total || 0), 0);
    buildTotalRow = () => {
      const row = Array(headers.length).fill("");
      row[0]  = "Grand Total:";
      row[headers.length - 1] = money(total);
      return row;
    };

  } else {
    buildTotalRow = () => {
      const t = list.reduce((acc, row) => {
        acc.subtotal += Number(row.subtotal     || 0);
        acc.discount += Number(row.discount     || 0);
        acc.vat      += Number(row.vat          || 0);
        acc.cgst     += Number(row.cgst_amount  || 0);
        acc.sgst     += Number(row.sgst_amount  || 0);
        acc.igst     += Number(row.igst_amount  || 0);
        acc.service  += Number(row.service      || 0);
        acc.total    += Number(row.total        || 0);
        acc.paid     += Number(row.paid         || 0);
        acc.due      += Number(row.due          || 0);
        return acc;
      }, { subtotal: 0, discount: 0, vat: 0, cgst: 0, sgst: 0, igst: 0, service: 0, total: 0, paid: 0, due: 0 });

      return [
        "Grand Total:", "", "", "", "",
        money(t.subtotal), money(t.discount), money(t.vat),
        ...(showGst ? [money(t.cgst), money(t.sgst), money(t.igst)] : []),
        money(t.service), money(t.total), money(t.paid), money(t.due),
      ];
    };
  }

  // ── Assemble ──────────────────────────────────────────────────
  const rows = [
    [title],
    [],
    headers.map(escape),
    ...list.map((row, i) => buildRow(row, i).map(escape)),
    [],
    buildTotalRow().map(escape),
  ];

  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};